import { RFQ } from '../types/rfq.types';
import { calculateWorkflowStep } from '../utils/workflowUtils';
import { displayRfqId } from '../utils/rfqIdentity';

/**
 * Pure mapping + derivation behind the dashboard hook: list items → table
 * rows, page-derived KPI fallback, and the attention-queue rules. No React,
 * no HTTP — `useDashboard` owns fetching/state and re-exports these types.
 */
export type RFQStage = 'Upload BOM' | 'Sourcing Criteria' | 'Supplier Discovery' | 'Supplier Outreach' | 'Quotes & Proposals';

/**
 * Table-filter dimensions (owner decisions, 28 Jul 2026): ownership My
 * projects / Everyone and stage / needs-review. The ownership default is
 * derived from the caller's own active-RFQ count rather than fixed — someone
 * with none of their own lands on Everyone, so a filter never hands anyone an
 * empty table; the control shows the derived value, so org data never renders
 * under a "Mine" label. There is deliberately NO status
 * toggle: "Active RFQs" always shows active only — awarded RFQs don't render
 * here (they'll surface in a future RFQs section / Robbie insights), matching
 * the summary's activeTotal.
 */
export type OwnerView = 'mine' | 'everyone';
export type StageFilter = 'all' | 'needs-review' | RFQStage;

/** Active = still in flight: not awarded, not cancelled. */
export const isActiveRfq = (rfq: DashboardRFQ): boolean => !rfq.awarded && rfq.status !== 'CANCELLED';

/**
 * THE "needs review" definition — one rule for the attention queue, the
 * "Needs review" quick filter, and the KPI fallback, mirrored by the backend
 * summary's `awaitingReview` (owner-caught inconsistency, 28 Jul 2026): real
 * work waiting on the buyer only. Stage-4 RFQs with zero supplier replies are
 * waiting on suppliers, not the buyer, and never count.
 */
export const needsBuyerReview = (rfq: DashboardRFQ): boolean =>
    rfq.recommendationReady || rfq.stageStep === 5 || (rfq.stageStep === 4 && rfq.repliesCount > 0);

/** One dashboard-table row, mapped from the org-wide list endpoint. */
export interface DashboardRFQ {
    /**
     * The DISPLAY id (`RFQ-079`), never the stored one (`RFQ-079-QPR2`).
     * This single field is printed by the table, seeds every `AttentionItem`,
     * and is what a row click routes on — so a stored id here puts the
     * organization's code in front of the user in three places at once.
     * `mapRfq` converts it; nothing downstream should convert it again.
     */
    id: string;
    /** Real project name — null when the RFQ was never named (render a muted placeholder, never the id twice). */
    name: string | null;
    stage: RFQStage;
    /** 1–5 across the active stages; the honest n/9 progress numerator. */
    stageStep: number;
    status: string;
    awarded: boolean;
    /** "You", the creator's stored name, or null (pre-`createdByName` RFQs). */
    owner: string | null;
    isMine: boolean;
    /** The BOM behind the row's research status; null before a BOM exists. */
    bomId?: string | null;
    partsCount: number;
    repliesCount: number;
    /** Award recommendation generated and not yet accepted. */
    recommendationReady: boolean;
    /** Technical sub-steps shared with the end user and not yet signed off. */
    pendingOnEndUser: boolean;
    /** End user's display name/email; null when none is named. */
    endUserLabel: string | null;
    /** The viewer is this RFQ's named end user (and not its creator). */
    iAmEndUser: boolean;
    createdAtIso: string;
    updatedAtIso: string;
}

/** Attention-queue entry — real, rule-based reasons only (no fabricated urgency). */
export interface AttentionItem {
    id: string;
    name: string;
    reason: 'quotes-review' | 'quotes-open' | 'supplier-replies' | 'end-user-fill' | 'end-user-pending';
    label: string;
    actionLabel: 'Review' | 'Open';
    /** Robbie-phrased insight chip — render slot only; populated by a later task. */
    insight?: string;
}

/** The five stages a buyer can actually be in today (rest are locked). */
export const ACTIVE_PIPELINE_STAGES: RFQStage[] = [
    'Upload BOM', 'Sourcing Criteria', 'Supplier Discovery', 'Supplier Outreach', 'Quotes & Proposals',
];

/**
 * KPI-row metrics. Source of truth is the server summary endpoint (exact,
 * org-wide aggregation); when it is unreachable the hook falls back to
 * deriving what it honestly can from the fetched RFQ page, leaving the
 * server-only metrics null (rendered as "—").
 */
export interface DashboardKpis {
    activeTotal: number;
    stagesInPlay: number;
    awaitingReview: number;
    oldestWaitingDays: number | null;
    suppliersEngaged: number | null;
    suppliersRespondedThisWeek: number | null;
    avgCycleDays: number | null;
    avgCycleDeltaPct: number | null;
    completedCount: number;
    awardedThisMonth: number;
}

const DAY_MS = 86_400_000;
// One page covers the whole org for now; beyond this the table would truncate
// (no pager UI yet) — an honest, documented limit rather than silent slicing.
export const PAGE_LIMIT = 100;

const STAGE_BY_STEP: RFQStage[] = ACTIVE_PIPELINE_STAGES;
const UNNAMED = 'Untitled RFQ'; // backend default when created without a name

/** Page-derived fallback used only when the summary endpoint is unreachable. */
export const deriveKpisFromPage = (rfqs: DashboardRFQ[], stageCounts: Record<string, number>): DashboardKpis => {
    const now = new Date();
    const daysSince = (iso: string) => Math.max(0, Math.floor((now.getTime() - new Date(iso).getTime()) / DAY_MS));
    const active = rfqs.filter(isActiveRfq);
    const awaiting = active.filter(needsBuyerReview);
    const completed = rfqs.filter(r => r.awarded);
    const cycleDays = (r: DashboardRFQ) =>
        Math.max(0, (new Date(r.updatedAtIso).getTime() - new Date(r.createdAtIso).getTime()) / DAY_MS);
    return {
        activeTotal: active.length,
        stagesInPlay: ACTIVE_PIPELINE_STAGES.filter(stage => (stageCounts[stage] || 0) > 0).length,
        awaitingReview: awaiting.length,
        oldestWaitingDays: awaiting.length ? Math.max(...awaiting.map(r => daysSince(r.updatedAtIso))) : null,
        suppliersEngaged: null,
        suppliersRespondedThisWeek: null,
        avgCycleDays: completed.length
            ? completed.reduce((sum, r) => sum + cycleDays(r), 0) / completed.length
            : null,
        avgCycleDeltaPct: null,
        completedCount: completed.length,
        awardedThisMonth: completed.filter(r =>
            new Date(r.updatedAtIso).getMonth() === now.getMonth() &&
            new Date(r.updatedAtIso).getFullYear() === now.getFullYear()
        ).length,
    };
};

export const mapRfq = (rfq: RFQ, myUserId?: string, myEmail?: string): DashboardRFQ => {
    const { step } = calculateWorkflowStep(rfq);
    const stageStep = Math.min(Math.max(step, 1), 5);
    const isMine = Boolean(myUserId && rfq.createdBy === myUserId);
    const endUserEmail = rfq.endUser?.email?.toLowerCase() || null;
    return {
        // The DISPLAY form, deliberately. This one field is both what the table
        // prints and what a row click routes on, and it seeds every
        // `AttentionItem` below (whose name falls back to the id for an unnamed
        // RFQ), so a stored id here surfaces the organization's code in three
        // places at once — which is exactly how it reached the dashboard.
        // Routing on the display form is safe: the backend resolver accepts
        // either form within the caller's organization.
        id: displayRfqId(rfq.rfqId),
        name: rfq.name && rfq.name !== UNNAMED ? rfq.name : null,
        stage: STAGE_BY_STEP[stageStep - 1],
        stageStep,
        status: rfq.status || 'DRAFT',
        awarded: rfq.status === 'AWARDED',
        owner: myUserId && rfq.createdBy === myUserId ? 'You' : rfq.createdByName || null,
        isMine,
        bomId: rfq.bomId ?? null,
        partsCount: rfq.partsCount ?? 0,
        repliesCount: rfq.supplierRepliesCount ?? 0,
        recommendationReady: Boolean(rfq.quoteRecommendation?.generatedAt && !rfq.quoteRecommendation?.acceptedAt),
        pendingOnEndUser: rfq.handoff?.status === 'PENDING',
        endUserLabel: (() => {
            const base = rfq.endUser?.name || rfq.endUser?.email || null;
            return base && rfq.endUser?.teamName ? `${base} (${rfq.endUser.teamName})` : base;
        })(),
        // Same identity rule as the wizard: email match, creator excluded.
        iAmEndUser: Boolean(endUserEmail && myEmail && endUserEmail === myEmail.toLowerCase() && !isMine),
        createdAtIso: rfq.createdAt,
        updatedAtIso: rfq.updatedAt,
    };
};

/**
 * Real attention reasons only — action-required states derived from stored
 * facts. Awarded RFQs are done, not attention (owner, 28 Jul; a "documents
 * ready" nudge belongs to Robbie insights later). Stage-4 RFQs without
 * replies are deliberately absent (in-flight outreach is not waiting on the
 * buyer), and "stalled" detection is not implemented: no entered-stage
 * timestamp exists, and flagging on updatedAt alone would misfire.
 */
export const deriveAttention = (rfqs: DashboardRFQ[]): AttentionItem[] => {
    const active = rfqs.filter(isActiveRfq);
    const byRecent = (a: DashboardRFQ, b: DashboardRFQ) =>
        new Date(b.updatedAtIso).getTime() - new Date(a.updatedAtIso).getTime();

    // The viewer's own fill work leads — it blocks the whole RFQ.
    const fillMine: AttentionItem[] = active
        .filter((rfq) => rfq.pendingOnEndUser && rfq.iAmEndUser)
        .sort(byRecent)
        .map((rfq) => ({
            id: rfq.id, name: rfq.name ?? rfq.id, reason: 'end-user-fill',
            label: 'Technical details waiting on you', actionLabel: 'Open',
        }));

    const review: AttentionItem[] = active
        .filter(needsBuyerReview)
        .sort(byRecent)
        .map((rfq): AttentionItem | null => {
            const name = rfq.name ?? rfq.id;
            if (rfq.recommendationReady) {
                return { id: rfq.id, name, reason: 'quotes-review', label: 'Award recommendation ready to review', actionLabel: 'Review' };
            }
            if (rfq.stageStep === 5) {
                return { id: rfq.id, name, reason: 'quotes-open', label: 'Quotes stage open — award plan not started', actionLabel: 'Open' };
            }
            if (rfq.stageStep === 4 && rfq.repliesCount > 0) {
                const replies = rfq.repliesCount === 1 ? '1 supplier reply' : `${rfq.repliesCount} supplier replies`;
                return { id: rfq.id, name, reason: 'supplier-replies', label: `${replies} to review`, actionLabel: 'Open' };
            }
            return null;
        })
        .filter((item): item is AttentionItem => item !== null);

    // RFQs parked with a named end user — shown so the buyer sees who holds
    // the ball (not their work, but their wait).
    const parked: AttentionItem[] = active
        .filter((rfq) => rfq.pendingOnEndUser && !rfq.iAmEndUser)
        .sort(byRecent)
        .map((rfq) => ({
            id: rfq.id, name: rfq.name ?? rfq.id, reason: 'end-user-pending',
            label: `Waiting on ${rfq.endUserLabel ?? 'the end user'} for technical details`, actionLabel: 'Open',
        }));

    const seen = new Set<string>();
    return [...fillMine, ...review, ...parked].filter((item) =>
        seen.has(item.id) ? false : (seen.add(item.id), true),
    );
};
